// GerenciamentoImoveisPage.js
import React, { useCallback, useEffect, useState } from 'react';
import {
  Pagination,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Select,
  MenuItem
} from '@mui/material';
import { Edit, ToggleOn, ToggleOff, Search, Apartment, Sync, Add, Visibility } from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import AppBarComponent from '../../../components/AppBarComponent';
import ImovelDetailDialog from '../../../components/realEstate/ImovelDetailDialog';

// Constantes para paginação
const DEFAULT_PAGE_SIZE = 10;
const PAGINATION_OPTIONS = [10, 25, 50];

// Componentes estilizados
const Container = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  margin: theme.spacing(2, 0),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
}));

const StatusBadge = styled(Chip)(({ status, theme }) => ({
  backgroundColor: status ? theme.palette.success.main : theme.palette.error.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
  borderRadius: theme.shape.borderRadius,
}));

const SearchField = styled(TextField)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(4),
  '& .MuiOutlinedInput-root': {
    height: '56px',
    fontSize: '1rem',
    borderRadius: theme.shape.borderRadius,
    paddingLeft: theme.spacing(1),
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    transform: 'translate(44px, 18px) scale(1)',
    '&.Mui-focused': {
      transform: 'translate(14px, -9px) scale(0.75)',
      color: theme.palette.primary.main,
    },
    '&.MuiFormLabel-filled': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiInputAdornment-root': {
    marginRight: theme.spacing(1),
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    borderColor: theme.palette.divider,
  },
  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[50],
    },
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    backgroundColor: theme.palette.action.hover,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
  '& svg': {
    fontSize: '4rem',
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
}));

const PropertyTypeBadge = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  '&.house': {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  '&.apartment': {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  '&.land': {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  '&.commercial': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,
  },
}));

const DescriptionText = styled(Typography)(({ theme }) => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '300px',
}));

export default function GerenciamentoImoveisPage() {
  const [imoveis, setImoveis] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [selectedImovel, setSelectedImovel] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const navigate = useNavigate();

  const buildApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
      console.error('REACT_APP_API_BASE_URL não está definido no arquivo .env');
      return endpoint;
    }
    const normalizedBase = baseUrl.replace(/\/+$/, '') + '/';
    return new URL(endpoint, normalizedBase).toString();
  };

  const fetchImoveis = useCallback(async (page, search = '') => {
    setIsLoading(true);
    setError(null);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!user.token) {
        throw new Error('Usuário não autenticado');
      }
      
      const params = {
        page,
        size: pagination.pageSize,
        ...(search && { searchTerm: search.trim() })
      };

      const url = buildApiUrl('real-estate/list');
      const response = await axios.get(url, {
        params,
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
      });
      
      if (!response.data?.data) {
        throw new Error('Estrutura de dados inválida na resposta');
      }

      setImoveis(response.data.data);
      setPagination(prev => ({
        ...prev,
        currentPage: response.data.meta?.currentPage || 1,
        totalPages: response.data.meta?.totalPages || 1,
        totalItems: response.data.meta?.totalItems || 0
      }));
    } catch (error) {
      console.error('Erro ao carregar imóveis:', error);
      setError(error.response?.data?.message || 
              error.message || 
              'Erro ao carregar imóveis. Verifique sua conexão e tente novamente.');
      
      setSnackbar({
        open: true,
        message: 'Erro ao carregar imóveis',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [pagination.pageSize]); 

  useEffect(() => {
    fetchImoveis(pagination.currentPage, appliedSearchTerm);
  }, [pagination.currentPage, appliedSearchTerm, fetchImoveis]);

  const handlePageChange = (_, page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({
      ...prev,
      pageSize: e.target.value,
      currentPage: 1
    }));
  };

  const handleEdit = (imovel) => {
    if (!imovel.status) {
      setSnackbar({
        open: true,
        message: 'Não é possível editar um imóvel inativo',
        severity: 'warning',
      });
      return;
    }
    
    const imovelData = {
      ...imovel,
      typology: imovel.typology,
      bathrooms: imovel.bathrooms || 0,
      price: imovel.price || 0,
      squareMeters: imovel.squareMeters || 0,
      address: {
        street: imovel.address?.street || '',
        number: imovel.address?.number || '',
        complement: imovel.address?.complement || '',
        neighborhood: imovel.address?.neighborhood || '',
        city: imovel.address?.city || '',
        state: imovel.address?.state || '',
        country: imovel.address?.country || '',
        zipCode: imovel.address?.zipCode || ''
      }
    };
    
    navigate(`/real-estate/update/${imovel.title}`, {
      state: { imovelData }
    });
  };

  const toggleImovelStatus = async (imovel) => {
  if (isToggling) return;
  
  const newStatus = !imovel.status;
  setIsToggling(true);
  
  try {
    // Atualização otimista com cópia profunda do array
    setImoveis(prevImoveis => {
      return prevImoveis.map(item => {
        // Comparação estrita por ID e título para garantir que estamos modificando o imóvel correto
        if (item.id === imovel.id && item.title === imovel.title) {
          return { ...item, status: newStatus };
        }
        return item;
      });
    });
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.token) throw new Error('Usuário não autenticado');
    
    const endpoint = newStatus ? 'enable' : 'disable';
    const url = buildApiUrl(`real-estate/${endpoint}/${encodeURIComponent(imovel.title)}`);
    
    const response = await axios.patch(url, {}, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${user.token}`,
      },
    });

    // Verifica se a resposta foi bem sucedida
    if (response.status >= 200 && response.status < 300) {
      setSnackbar({
        open: true,
        message: `Imóvel "${imovel.title}" ${newStatus ? 'ativado' : 'desativado'} com sucesso!`,
        severity: 'success',
      });
      
      // Força atualização dos dados após 1 segundo para garantir sincronização
      // setTimeout(() => {
      //   fetchImoveis(pagination.currentPage, appliedSearchTerm);
      // }, 1000);
    } else {
      throw new Error('Resposta não bem sucedida');
    }
  } catch (error) {
    console.error('Erro ao alterar status:', error);
    
    // Reverte para o estado anterior
    setImoveis(prevImoveis => prevImoveis.map(item => {
      if (item.id === imovel.id && item.title === imovel.title) {
        return { ...item, status: !newStatus };
      }
      return item;
    }));
    
    setSnackbar({
      open: true,
      message: error.response?.data?.message || `Erro ao ${newStatus ? 'ativar' : 'desativar'} o imóvel "${imovel.title}"`,
      severity: 'error',
    });
  } finally {
    setIsToggling(false);
  }
};
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatPropertyType = (type) => {
    const types = {
      'house': 'Casa',
      'apartment': 'Apartamento',
      'land': 'Terreno',
      'commercial': 'Comercial'
    };
    return types[type] || type;
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return `${address.street || ''}, ${address.number || ''}${address.complement ? ` (${address.complement})` : ''}`;
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const truncateDescription = (description, maxLength = 100) => {
    if (!description) return 'N/A';
    return description.length > maxLength 
      ? `${description.substring(0, maxLength)}...` 
      : description;
  };

  const handleOpenDetails = (imovel) => {
    setSelectedImovel(imovel);
    setDetailOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailOpen(false);
    setSelectedImovel(null);
  };

  return (
    <section>
      <AppBarComponent title="Gerenciamento de Imóveis" />
      <Container elevation={0}>
        <SearchField
          fullWidth
          variant="outlined"
          label="Buscar imóveis"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
        />

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <EmptyState>
            <Typography color="error" variant="h6" gutterBottom>
              {error}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => fetchImoveis(pagination.currentPage, appliedSearchTerm)}
              startIcon={<Sync />}
            >
              Tentar novamente
            </Button>
          </EmptyState>
        ) : imoveis.length === 0 ? (
          <EmptyState>
            <Apartment fontSize="inherit" />
            <Typography variant="h6" color="textSecondary">
              {appliedSearchTerm 
                ? 'Nenhum imóvel encontrado com esse critério de busca.' 
                : 'Nenhum imóvel cadastrado.'}
            </Typography>
            {!appliedSearchTerm && (
              <Button 
                variant="outlined" 
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => navigate('/properties/register')}
                startIcon={<Add />}
              >
                Adicionar primeiro imóvel
              </Button>
            )}
          </EmptyState>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" color="textSecondary">
                Mostrando {imoveis.length} de {pagination.totalItems} imóveis
              </Typography>
              
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="textSecondary">
                  Itens por página:
                </Typography>
                <Select
                  value={pagination.pageSize}
                  onChange={handlePageSizeChange}
                  size="small"
                  sx={{ minWidth: 80 }}
                >
                  {PAGINATION_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Endereço</TableCell>
                    <TableCell>Preço</TableCell>
                    <TableCell>Área (m²)</TableCell>
                    <TableCell>Quartos</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell width="180px" align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {imoveis.map((imovel) => (
                    <TableRow key={imovel.id} hover>
                      <TableCell>
                        <Typography fontWeight="500">
                          {imovel.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <PropertyTypeBadge
                          label={formatPropertyType(imovel.type)}
                          className={imovel.type}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={imovel.description || 'Nenhuma descrição disponível'} arrow>
                          <DescriptionText>
                            {truncateDescription(imovel.description)}
                          </DescriptionText>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {formatAddress(imovel.address)}
                        <Typography variant="caption" display="block" color="textSecondary">
                          {imovel.address?.neighborhood || 'N/A'}, {imovel.address?.city || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="500">
                          {formatCurrency(imovel.price)}
                        </Typography>
                      </TableCell>
                      <TableCell>{imovel.squareMeters || 'N/A'}</TableCell>
                      <TableCell>{imovel.typology || 'N/A'}</TableCell>
                      <TableCell>
                        <StatusBadge 
                          label={imovel.status ? 'Ativo' : 'Inativo'} 
                          status={imovel.status} 
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={1}>
                          <Tooltip title="Visualizar detalhes">
                            <ActionButton
                              color="info"
                              onClick={() => handleOpenDetails(imovel)}
                            >
                              <Visibility />
                            </ActionButton>
                          </Tooltip>
                          
                          <Tooltip title={imovel.status ? "Editar imóvel" : "Imóvel inativo - não pode ser editado"}>
                            <span>
                              <ActionButton
                                color="primary"
                                onClick={() => handleEdit(imovel)}
                                disabled={!imovel.status}
                              >
                                <Edit />
                              </ActionButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title={imovel.status ? "Desativar imóvel" : "Ativar imóvel"}>
                            <ActionButton
                              color={imovel.status ? "error" : "success"}
                              onClick={() => toggleImovelStatus(imovel)}
                              disabled={isToggling}
                            >
                              {imovel.status ? <ToggleOff /> : <ToggleOn />}
                            </ActionButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </Box>

            <Box mt={4} display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Página {pagination.currentPage} de {pagination.totalPages}
              </Typography>
              
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                disabled={isLoading}
                showFirstButton
                showLastButton
                showPrevButton
                showNextButton
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '0.875rem',
                    minWidth: 32,
                    height: 32,
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                  }
                }}
              />
            </Box>
          </>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            sx={{ width: '100%' }}
            elevation={6}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <ImovelDetailDialog
          open={detailOpen}
          onClose={handleCloseDetails}
          imovel={selectedImovel}
          formatPropertyType={formatPropertyType}
          formatCurrency={formatCurrency}
        />
      </Container>
    </section>
  );
}